import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { resendWelcomeEmail, resetPassword, Tenant } from '@steelbuy/api-integration';
import { SetPasswordForm } from '../../set-password-form/SetPasswordForm';

import './ResetPasswordForm.scss';

interface ResetPasswordFormProps {
    token: string;
    onSuccess: (username: string) => void;
    forgotPasswordUrl: string;
    tenant: Tenant;
    apiBaseUrl: string;
    isSetNewPassword?: boolean;
    email?: string;
}

export const ResetPasswordForm = ({
    token,
    onSuccess,
    forgotPasswordUrl,
    tenant,
    apiBaseUrl,
    isSetNewPassword = false,
    email,
}: ResetPasswordFormProps) => {
    const [isLoading, setLoading] = useState(false);
    const { t } = useTranslation('uiDomain');
    const classPrefix = 'reset-password-form';
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const onResendEmail = async () => {
        email && (await resendWelcomeEmail(apiBaseUrl, encodeURIComponent(email)));
        navigate(forgotPasswordUrl, {
            state: {
                defaultSuccessNotification: t('login.tempPasswordEmailSent', {
                    username: email,
                }),
            },
        });
    };

    const onSubmit = async (newPassword: string) => {
        setError('');
        setLoading(true);
        try {
            const username = await resetPassword(apiBaseUrl, newPassword, token, tenant, isSetNewPassword);
            onSuccess(username);
        } catch (e) {
            setError(
                isSetNewPassword ? t('login.createNewPassword.tokenExpired') : t('login.resetPassword.tokenExpired')
            );
        }
        setLoading(false);
    };

    return (
        <main className={`${classPrefix} ${classPrefix}--bg`}>
            <header className={`${classPrefix}__header`}>
                <div>{isSetNewPassword ? t('login.createNewPassword.header') : t('login.resetPassword.header')}</div>
            </header>
            <section className={`${classPrefix}__header--subheading`}>
                {email && (
                    <div className={`${classPrefix}__email`}>{t('login.createNewPassword.description', { email })}</div>
                )}
                <div>
                    {t(`login.challengeRespond.forcePasswordChange.subheading.p2`)}
                    <br />
                    {t(`login.challengeRespond.forcePasswordChange.subheading.p3`)}
                </div>
            </section>
            <SetPasswordForm
                onSubmit={onSubmit}
                isLoading={isLoading}
                error={error}
                isSetNewPassword={isSetNewPassword}
                notificationLink={
                    isSetNewPassword
                        ? {
                              label: t('login.resetPassword.resendEmail'),
                              onClick() {
                                  onResendEmail();
                              },
                          }
                        : {
                              label: t('login.resetPassword.resendEmail'),
                              to: forgotPasswordUrl,
                          }
                }
            />
        </main>
    );
};
